import { differenceInYears } from 'date-fns';
import React, { useMemo } from 'react';

import { useAppSelector } from '@/hooks/redux';

import { selectExistingCustomerConfigs } from '@/redux/configs/selectors';

import { AccountTypeDetails } from '@/types/api/account';
import { User } from '@/types/user';

import { isExistingCustomer } from '@/utils/user';

import { AccountTypeCard } from '../components/AccountTypeCard';
import MaxAccountsPerTypeAlert from '../components/MaxAccountsPerTypeAlert';
import { getAccountsWithCheckingLimit } from '../utils/account';
import { isCaymanAccountEligible } from '../utils/caymanAccount';
import { AccountTypeHOCProps } from './BaseAccountTypeHOC';

type Props = AccountTypeHOCProps & {
  user: User;
};

const CaymanAccountTypeHOC: React.FC<Props> = ({
  accountTypes,
  user,
  selectedAccountTypes,
  onSeeDetails,
  toggleSelect,
}) => {
  const existingCustomerConfigs = useAppSelector(selectExistingCustomerConfigs);

  const accountTypesOptions: AccountTypeDetails[] = useMemo(() => {
    const age = user.birthday
      ? differenceInYears(new Date(), new Date(user.birthday))
      : 0;

    return accountTypes
      .map(type => {
        const isEligible = isCaymanAccountEligible(type, age);

        return {
          ...type,
          isEligible,
          reachedMaxAccountsPerType: false,
        };
      })
      .sort((a, b) => {
        // Sort by isEligible -> name -> currency
        if (a.isEligible && !b.isEligible) return -1;
        if (!a.isEligible && b.isEligible) return 1;

        const nameCompare = a.name.localeCompare(b.name);
        if (nameCompare !== 0) return nameCompare;

        if ((a.feeCurrency || '') < (b.feeCurrency || '')) return -1;
        if ((a.feeCurrency || '') > (b.feeCurrency || '')) return 1;
        return 0;
      });
  }, [user, accountTypes]);

  return getAccountsWithCheckingLimit({
    accounts: accountTypesOptions,
    selectedAccounts: selectedAccountTypes,
    maxAccountsPerType: isExistingCustomer(user)
      ? existingCustomerConfigs?.maxAccountsPerType
      : -1,
    requiredSort: true,
    userAccountTypes: user.accountTypes || [],
  }).map((item, index) => (
    <AccountTypeCard
      key={item.id}
      data={item}
      isDisabled={item.reachedMaxAccountsPerType}
      isSelected={selectedAccountTypes.some(account => account.id === item.id)}
      onSeeDetailsClick={data => onSeeDetails(data, index)}
      onClick={toggleSelect}
      warningMessage={
        item.reachedMaxAccountsPerType ? <MaxAccountsPerTypeAlert /> : null
      }
    />
  ));
};

export default CaymanAccountTypeHOC;
